import React from "react";
import { Link } from "react-router-dom";
import MenuItem from "@material-ui/core/MenuItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import OSSIcon from "../OSSIcon/OSSIcon";

import { OSSAppDropdownClass } from "./OSSAppDropdownClass";

import * as tenants from "@oss/tenants";
import { el } from "date-fns/locale";

const useStyles = makeStyles(theme => ({
  menuItems: {
    padding: [[4, 16]]
  },
  museoSans: {
    color: theme.palette.common.black,
    fontFamily: theme.typography.brand.fontFamily,
    fontSize: 14
  },
  linkElements: {
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(4),
    "& svg": {
      marginRight: theme.spacing(1)
    }
  },
  listSubHeader: {
    marginTop: theme.spacing(1),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #ccc",
    lineHeight: 2,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(2),
    textAlign: "right"
  }
}));

/**
 * OSSAppDropdown
 * Requires the use of the user.allowed_urls object
 * Checks these urls against the tenant objects to render logos and app names
 * @param {object} user
 * @param {function} closeHandler
 *
 * @returns array of list menu items
 */
const OSSAppDropdown = ({ closeHandler, user }) => {
  const classes = useStyles();
  const theme = useTheme();

  const travelToNewRoute = ({ app, brandname, env }) => {
    closeHandler();

    const localhostPorts = {
      access: 3001,
      facebook: 3003,
      youtube: 3004,
      tiktok: 3007,
      targeting: 3006
    };

    // IF there is a custom hostname associated with desired route
    if (app.hostname) {
      // We override the intended hostname with the custom one
      // i.e. dev-youtube -> dev-targeting
      // in the case of localhost environments,
      //  we need to replace the localhost url with the new port number
      let hostname;

      if (env.url.includes("localhost")) {
        hostname = env.url.replace(
          new URL(env.url).port,
          localhostPorts[app.hostname]
        );
      } else {
        hostname = `${env.url.replace(brandname, app.hostname)}`;
      }
      return window.open(hostname, "_blank");
    }
    // Otherwise head to the intended hostname and pathname
    return window.open(env.url + app.url, "_blank");
  };

  const dropdownConfig = new OSSAppDropdownClass(user, tenants, theme);
  dropdownConfig.generateAppAndRouteConfigs();

  const { TenantIcon } = tenants;
  const menuItemsArray = Object.values(dropdownConfig?.routeObject);

  // Map through the menuItems array
  console.log(menuItemsArray);
  return menuItemsArray && menuItemsArray?.length > 0
    ? menuItemsArray.map(({ brand, routes, hostname }) => (
        <React.Fragment key={brand?.codename}>
          {/* And map through the hostnames array. Prod defaults to one per provider */}
          {hostname?.map(env => (
            <React.Fragment key={env?.name}>
              <ListSubheader
                disableSticky
                className={classes.listSubHeader}
                title={env?.name}
                data-appname={env.name}
              >
                {brand?.codename === "access" ? (
                  <OSSIcon
                    name="tools"
                    // showWords={false}
                    size="small"
                    style={{
                      height: 25,
                      width: 25
                    }}
                  />
                ) : (
                  <TenantIcon
                    name={brand.logoProps.name}
                    role="img"
                    width={25}
                    height={25}
                    fill={brand.color}
                  />
                )}
                {/* show env name if there is more than 1 environment (Dev), else show brand name */}
                <Typography variant="subtitle2">
                  {hostname?.length > 1
                    ? env?.name?.toLowerCase()?.split(brand.codename)[0]
                    : brand.name}
                </Typography>
              </ListSubheader>
              {routes?.map(app =>
                !app?.hostname && env?.url?.includes(window.location.host) ? (
                  <Link
                    to={app?.url}
                    style={{ textDecoration: "none" }}
                    onClick={closeHandler}
                  >
                    <MenuItem
                      key={`${brand?.codename}-${app?.url}`}
                      value={`${brand?.codename}-${app?.url}`}
                      className={classes.menuItems}
                      data-parentnav={env.name}
                    >
                      <div
                        className={[
                          classes.museoSans,
                          classes.linkElements
                        ].join(" ")}
                      >
                        <OSSIcon
                          name={app?.icon ?? "tools"}
                          {...(app?.icon === "openSlateLogo" && {
                            showWords: false
                          })}
                          size="small"
                          color={app?.color}
                          style={{
                            height: 20,
                            width: 20
                          }}
                        />
                        {/* This will style the OS text at the beginning of the app name with appropriate colors */}
                        {app?.title?.slice(0, 2) === "OS" ? (
                          <>
                            <span
                              style={{
                                color: app?.color,
                                marginRight: theme.spacing(0.5),
                                fontWeight: "bold"
                              }}
                            >
                              OS
                            </span>
                            {app?.title?.split("OS")}
                          </>
                        ) : (
                          app?.title
                        )}
                      </div>
                    </MenuItem>
                  </Link>
                ) : (
                  <MenuItem
                    key={`${brand?.codename}-${app?.url}`}
                    value={`${brand?.codename}-${app?.url}`}
                    className={classes.menuItems}
                    data-parentnav={env.name}
                    onClick={() =>
                      travelToNewRoute({
                        app,
                        brandname: brand.codename,
                        env
                      })
                    }
                  >
                    <div
                      className={[classes.museoSans, classes.linkElements].join(
                        " "
                      )}
                    >
                      <OSSIcon
                        name={app?.icon ?? "tools"}
                        {...(app?.icon === "openSlateLogo" && {
                          showWords: false
                        })}
                        size="small"
                        color={app?.color}
                        style={{
                          height: 20,
                          width: 20
                        }}
                      />
                      {/* This will style the OS text at the beginning of the app name with appropriate colors */}
                      {app?.title?.slice(0, 2) === "OS" ? (
                        <>
                          <span
                            style={{
                              color: app?.color,
                              marginRight: theme.spacing(0.5),
                              fontWeight: "bold"
                            }}
                          >
                            OS
                          </span>
                          {app?.title?.split("OS")}
                        </>
                      ) : (
                        app?.title
                      )}
                    </div>
                  </MenuItem>
                )
              )}
            </React.Fragment>
          ))}
        </React.Fragment>
      ))
    : null;
};

export default OSSAppDropdown;
